import { useEffect, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { FORMS, submitAuthForm, AuthSubmissionError } from '@/lib/authForm'
import FlipButton from '@/components/ui/FlipButton'

/*
  MIG Sign-in page (no Figma — matches the existing form-heavy pages
  Contact and Lease Enquiry: 180px alfarn-2 title, Poppins Light body
  and inputs, black-on-white, single primary submit button).

  Auth happens via the platformOS `modules/portal/sign_in` form, which
  the Liquid wrapper renders hidden as #auth-form-sign-in. On submit
  we fire `submitAuthForm('signIn', { ... })` which builds a fresh
  POST form with the captured authenticity_token + form_id and the
  user-supplied email/password/redirect/guest_temp_uuid fields. The
  server validates and either redirects to `redirect` (success) or
  bounces back with `flash_alert` set (failure) — that flash is
  surfaced into this page's payload so the inline error renders.

  Styling lives in src/styles/pages/auth.css. The submit button's
  pending state is driven by `data-pending` on the button.
*/

interface SignInPayload {
  return_to: string
  guest_temp_uuid: string | null
  flash_alert: string | null
  flash_notice: string | null
}

interface FormState {
  email: string
  password: string
}

export default function SignIn() {
  const payload = usePageData<SignInPayload>() ?? {
    return_to: '/marketplace',
    guest_temp_uuid: null,
    flash_alert: null,
    flash_notice: null,
  }

  const [visible, setVisible] = useState(false)
  const [state, setState] = useState<FormState>({ email: '', password: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(payload.flash_alert ?? null)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  function onChange<K extends keyof FormState>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setState((prev) => ({ ...prev, [key]: e.target.value }))
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!state.email.trim() || !state.password) {
      setError('Please enter your email and password.')
      return
    }
    if (!FORMS.signIn) {
      setError('Sign-in is not available right now. Please refresh and try again.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      // Guard against bouncing post-purchase buyers back to /confirmation
      // on their next sign-in. The order-confirmation page is a one-time
      // post-purchase view; the first few logins after creating an
      // account from /confirmation should land on the homepage instead.
      const safeReturn = (() => {
        const candidate = payload.return_to ?? ''
        if (candidate.startsWith('/confirmation')) return '/'
        return candidate || '/marketplace'
      })()
      const { redirectTo } = await submitAuthForm('signIn', {
        'form[email]': state.email.trim(),
        'form[password]': state.password,
        'form[redirect]': safeReturn,
        'form[guest_temp_uuid]': payload.guest_temp_uuid ?? '',
      })
      const safeRedirect =
        redirectTo && redirectTo.startsWith('/confirmation') ? '/' : redirectTo
      window.location.href = safeRedirect || safeReturn
    } catch (err) {
      const msg =
        err instanceof AuthSubmissionError
          ? err.message === 'Submission failed.'
            ? 'Invalid email or password.'
            : err.message
          : 'Sign-in failed. Please try again.'
      setError(msg)
      setSubmitting(false)
    }
  }

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-signin-page-title">Sign in</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate className="mig-form mig-auth-form">
        {payload.flash_notice && (
          <p role="status" className="mig-auth-body">
            {payload.flash_notice}
          </p>
        )}

        <FieldRow label="EMAIL ADDRESS">
          <input
            type="email"
            name="email"
            autoComplete="email"
            placeholder="Enter email address"
            value={state.email}
            onChange={onChange('email')}
            disabled={submitting}
            className="mig-auth-input"
          />
        </FieldRow>

        <FieldRow label="PASSWORD">
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="Enter password"
            value={state.password}
            onChange={onChange('password')}
            disabled={submitting}
            className="mig-auth-input"
          />
        </FieldRow>

        <div className="mig-auth-bottom-row">
          <a href="/forgot-password" className="mig-auth-forgot-link">
            Forgot password?
          </a>
          <FlipButton
            type="submit"
            disabled={submitting}
            className="mig-auth-submit"
            data-pending={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </FlipButton>
        </div>

        {error && (
          <p role="alert" className="mig-auth-error">{error}</p>
        )}

        <div className="mig-page-back-row">
          <a href={payload.return_to ?? '/marketplace'} className="mig-auth-back">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </form>
    </section>
  )
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mig-auth-field-row">
      <div className="mig-auth-field-inner">
        <label className="mig-auth-field-label">{label}</label>
        {children}
      </div>
      <div className="mig-divider-line" />
    </div>
  )
}
